import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment'

@Injectable()
export class MyInterceptor implements HttpInterceptor {

  /**
   * Give every API response a `data` array, whatever the backend sent.
   *
   * The API does not use one shape for "nothing found". A missing service
   * answers `{status:2, data:"false"}` - `data` as the *string* "false", so
   * `data[0]` is the character "f" and `data.forEach` is not a function. A
   * missing article answers `{status:0, message:"no news found"}` with no
   * `data` key at all, so `data[0]` throws outright.
   *
   * Components read `res.data[0]` in 1,260 places and almost none check
   * `status` first, so any detail page reached with a stale or wrong slug
   * throws - which is what old shared links and crawlers do. Normalising here
   * rather than in 1,260 places means `data[0]` is simply `undefined` and
   * `forEach`/`filter` iterate nothing.
   *
   * `status` and `message` are passed through untouched, so code that does
   * check them is unaffected. Nothing in the codebase branches on
   * `data == "false"` - checked before doing this.
   */
  private normaliseData(body: any): any {
    if (!body || typeof body !== 'object' || Array.isArray(body)) { return body; }
    if (!('data' in body)) { return { ...body, data: [] }; }
    const d = body.data;
    if (Array.isArray(d)) { return body; }
    // "false", false, null, "" - every way this API says "nothing"
    if (d === 'false' || d === false || d === null || d === undefined || d === '') {
      return { ...body, data: [] };
    }
    return body;   // a real object payload stays an object
  }

  intercept(request: HttpRequest<any>, next: HttpHandler):  Observable<HttpEvent<any>> {
    // Only our own API gets our auth headers. They were added to every request,
    // including the one to graph.instagram.com for the footer Instagram strip:
    // Instagram's CORS preflight rejects an unknown 'x-rak-key' header, so that
    // request failed and the strip never loaded. Sending x-rak-key to a third
    // party also leaks it. Third-party requests now go through untouched.
    if (request.url.startsWith(environment.cms_datas.base_url)) {
      const setHeaders: { [name: string]: string } = {
        'x-rak-key': environment.cms_datas.rakKey
      };
      // Attach the signed session token so member-scoped endpoints can derive the
      // caller's identity server-side, instead of trusting a member_id in the body.
      try {
        const raw = localStorage.getItem('RAKSME_TOKEN');
        if (raw) {
          const item = JSON.parse(raw);
          if (item && item.value) {
            setHeaders['X-Auth-Token'] = item.value;
          }
        }
      } catch (e) { /* no token yet (public pages) - send x-rak-key only */ }
      request = request.clone({ setHeaders });
    }
    return next.handle(request).pipe(
      map(event => {
        if (event instanceof HttpResponse) {
          return event.clone({ body: this.normaliseData(event.body) });
        }
        return event;
      })
    );
  }
}

//application/json